import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import LoadingSpinner from 'component/LoadingSpinner';
import { encryptPassword } from 'helpers/EncryptPassword';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import CustomDropdown from 'component/CustomDropdown';
import ProjectUserService from 'service/ClientTransmoraService/ProjectUserService';
import { ProjectUserInviteReq } from 'domain/payload/request/clientTransmora/projectUser/ProjectUserInviteReq';
import { ProjectUserUpdateReq } from 'domain/payload/request/clientTransmora/projectUser/ProjectUserUpdateReq';

const ProjectUserForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();

  const isAddMode = id === '0';
  const projectUserService = ProjectUserService();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [clientId, setClientId] = useState('');
  const [initialPassword, setInitialPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [role, setRole] = useState<string[]>([]);

  // For edit mode: user status & created date
  const [status, setStatus] = useState('Active');
  const [createdAt, setCreatedAt] = useState('');
  const [changePassword, setChangePassword] = useState(false);

  const [autoGeneratePassword, setAutoGeneratePassword] = useState(true);
  const [sendInvitationEmail, setSendInvitationEmail] = useState(true);

  const [roleOptions, setRoleOptions] = useState<{ id: string; code: string; name: string }[]>([]);
  const [clientOptions, setClientOptions] = useState<{ id: string; name: string }[]>([]);

  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const handleAutoGenerateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAutoGeneratePassword(checked);

    if (checked) {
      setInitialPassword('');
      setConfirmPassword('');
      setSendInvitationEmail(true);
    } else {
      setSendInvitationEmail(false);
    }
  };

  const handleSendInvitationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoGeneratePassword && !e.target.checked) {
      return;
    }
    setSendInvitationEmail(e.target.checked);
  };

  const fetchConfig = async () => {
    const token = profile?.data?.token;
    if (!token) return;

    setIsLoadingConfig(true);
    try {
      // Try unified config endpoint first
      const res = await projectUserService.projectUserConfig(token);
      if (res.data) {
        setRoleOptions(
          res.data.roleList?.map((r) => ({
            id: r.id,
            code: r.code || r.id,
            name: r.name,
          })) ?? []
        );
        setClientOptions(res.data.clientList ?? []);
      }
    } catch (err: any) {
      console.warn('Unified config endpoint failed, falling back to separate configs...', err);
      // Fallback to separate endpoints
      try {
        const [roleRes, clientRes] = await Promise.all([
          projectUserService.projectUserRoleConfig(token),
          projectUserService.projectUserClientConfig(token),
        ]);
        setRoleOptions(
          roleRes.data?.map((r: any) => ({
            id: r.id,
            code: r.code || r.id,
            name: r.name,
          })) ?? []
        );
        setClientOptions(
          clientRes.data?.map((c: any) => ({
            id: c.id,
            name: c.name,
          })) ?? []
        );
      } catch (fallbackErr: any) {
        console.error('Fallback config failed:', fallbackErr);
        const msg =
          typeof fallbackErr === 'string'
            ? fallbackErr
            : fallbackErr.response?.data?.message || fallbackErr.message || 'Gagal memuat konfigurasi';
        window.alert(`Error: ${msg}`);
      }
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data?.token;
    if (!token || !id || id === '0') return;

    setIsLoadingDetail(true);
    try {
      const res = await projectUserService.projectUserDetail(token, id);
      if (res.data) {
        setEmail(res.data.email || '');
        setName(res.data.name || '');
        setPhone(res.data.phone || '');
        setClientId(res.data.clientId || '');
        setStatus(res.data.status || (res.data.active ? 'Active' : 'Inactive'));
        setCreatedAt(res.data.createdAt || '');

        if (res.data.roles && Array.isArray(res.data.roles)) {
          setRole(res.data.roles.map((r: any) => r.id));
        }
      }
    } catch (err: any) {
      console.error('Error fetching detail:', err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail Client Project User';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    if (!isAddMode) {
      fetchDetail();
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!clientId) {
      window.alert('Silakan pilih Client');
      return;
    }

    if (role.length === 0) {
      window.alert('Silakan pilih minimal 1 role untuk user');
      return;
    }

    if (isAddMode) {
      if (!autoGeneratePassword) {
        if (!initialPassword || !confirmPassword) {
          window.alert('Silakan isi field password');
          return;
        }

        if (initialPassword !== confirmPassword) {
          window.alert('Password dan Confirm Password tidak cocok');
          return;
        }

        if (initialPassword.length < 12) {
          window.alert('Password minimal harus 12 karakter');
          return;
        }
      }
    } else if (changePassword && !autoGeneratePassword) {
      if (!initialPassword || !confirmPassword) {
        window.alert('Silakan isi field password baru');
        return;
      }

      if (initialPassword !== confirmPassword) {
        window.alert('Password dan Confirm Password baru tidak cocok');
        return;
      }

      if (initialPassword.length < 12) {
        window.alert('Password baru minimal harus 12 karakter');
        return;
      }
    }

    setIsLoadingSave(true);

    try {
      if (isAddMode) {
        const payload: ProjectUserInviteReq = {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          clientId: clientId,
          roleIds: role,
          autoGenerate: autoGeneratePassword,
          password: autoGeneratePassword ? undefined : encryptPassword(initialPassword),
          sendEmail: sendInvitationEmail,
        };

        await projectUserService.projectUserInvite(token, payload);
        window.alert(`User ${email} berhasil diundang!`);
        navigate('/client-project-user');
      } else {
        const payload: ProjectUserUpdateReq = {
          id: id!,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          clientId: clientId,
          roleIds: role,
          ...(changePassword
            ? {
                autoGenerate: autoGeneratePassword,
                password: autoGeneratePassword ? undefined : encryptPassword(initialPassword),
              }
            : {}),
        };

        await projectUserService.projectUserUpdate(token, payload);
        window.alert(`User ${email} berhasil diperbarui!`);
        navigate('/client-project-user');
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err.response?.data?.message || err.message || 'Terjadi kesalahan saat menyimpan data';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const computedRoleOptions = roleOptions.map((r) => ({
    ...r,
    code: r.id,
  }));

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-0">{isAddMode ? 'Add Client Project User' : 'Edit Client Project User'}</h4>
          </div>
          {!isAddMode && createdAt && (
            <div className="text-end">
              <span className={`badge ${status === 'Active' ? 'bg-success' : 'bg-secondary'} me-2`}>
                {status}
              </span>
              <span className="text-muted small">Created: {createdAt}</span>
            </div>
          )}
        </div>

        <div className="p-4" style={{ background: 'white', borderRadius: '8px' }}>
          {isLoadingDetail || isLoadingConfig ? (
            <div className="py-5 text-center">
              <LoadingSpinner />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Left Column: Basic Information */}
                <div className="col-md-6 border-end">
                  <h5 className="mb-4 text-primary">Basic Information</h5>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label fw-semibold">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="form-control form-control-compact"
                      value={name}
                      placeholder="Enter full name"
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label fw-semibold">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      id="phone"
                      className="form-control form-control-compact"
                      value={phone}
                      placeholder="e.g. 081234567890"
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="roles" className="form-label fw-semibold">
                      Customer Roles <span className="text-danger">*</span>
                    </label>
                    <MultiSelectDropdown
                      options={computedRoleOptions}
                      value={role}
                      onChange={setRole}
                      placeholder="Select Customer Roles"
                    />
                    <div className="form-text small">Select one or more customer project roles.</div>
                  </div>
                </div>

                {/* Right Column: Client & Credentials */}
                <div className="col-md-6 ps-md-4">
                  <h5 className="mb-4 text-primary">Client & Credentials</h5>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Client <span className="text-danger">*</span>
                    </label>
                    <CustomDropdown
                      value={clientId}
                      options={clientOptions.map((c) => ({
                        id: c.id,
                        name: c.name,
                      }))}
                      onChange={(option) => setClientId(String(option.id || ''))}
                      placeholder="-- Select Client --"
                      searchable={true}
                      searchPlaceholder="Search Client..."
                      containerClassName="w-100"
                      buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="email" className="form-label fw-semibold">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="form-control form-control-compact"
                      value={email}
                      placeholder="user@client.com"
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {/* Password section in Add mode */}
                  {isAddMode && (
                    <div className="p-3 mb-3 bg-light rounded">
                      <div className="mb-3">
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="autoGeneratePassword"
                            checked={autoGeneratePassword}
                            onChange={handleAutoGenerateChange}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="autoGeneratePassword">
                            Auto Generate Password
                          </label>
                        </div>
                        <div className="form-text small">
                          Generates a secure 12-character random password on the server.
                        </div>
                      </div>

                      {!autoGeneratePassword && (
                        <>
                          <div className="mb-3">
                            <label htmlFor="initialPassword" className="form-label fw-semibold">
                              Password <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                id="initialPassword"
                                className="form-control form-control-compact"
                                value={initialPassword}
                                placeholder="Min. 12 characters"
                                onChange={(e) => setInitialPassword(e.target.value)}
                                required={!autoGeneratePassword}
                                minLength={12}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                              >
                                <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label htmlFor="confirmPassword" className="form-label fw-semibold">
                              Confirm Password <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                className="form-control form-control-compact"
                                value={confirmPassword}
                                placeholder="Confirm password"
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required={!autoGeneratePassword}
                                minLength={12}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                tabIndex={-1}
                              >
                                <i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      <div className="mb-1">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="sendInvitationEmail"
                            checked={sendInvitationEmail}
                            onChange={handleSendInvitationChange}
                            disabled={autoGeneratePassword}
                          />
                          <label className="form-check-label" htmlFor="sendInvitationEmail">
                            Send Invitation Email with credentials
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Password section in Edit mode */}
                  {!isAddMode && (
                    <div className="p-3 mb-3 bg-light rounded">
                      <div className="mb-2">
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="changePasswordToggle"
                            checked={changePassword}
                            onChange={(e) => {
                              setChangePassword(e.target.checked);
                              if (!e.target.checked) {
                                setInitialPassword('');
                                setConfirmPassword('');
                                setAutoGeneratePassword(false);
                              }
                            }}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="changePasswordToggle">
                            Reset / Update Password
                          </label>
                        </div>
                      </div>

                      {changePassword && (
                        <div className="mt-3">
                          <div className="mb-3">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="autoGeneratePasswordEdit"
                                checked={autoGeneratePassword}
                                onChange={handleAutoGenerateChange}
                              />
                              <label className="form-check-label" htmlFor="autoGeneratePasswordEdit">
                                Auto Generate New Password
                              </label>
                            </div>
                          </div>

                          {!autoGeneratePassword && (
                            <>
                              <div className="mb-3">
                                <label htmlFor="newPassword" className="form-label fw-semibold">
                                  New Password <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="newPassword"
                                    className="form-control form-control-compact"
                                    value={initialPassword}
                                    placeholder="Min. 12 characters"
                                    onChange={(e) => setInitialPassword(e.target.value)}
                                    required={changePassword && !autoGeneratePassword}
                                    minLength={12}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                  >
                                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                  </button>
                                </div>
                              </div>

                              <div className="mb-3">
                                <label htmlFor="confirmNewPassword" className="form-label fw-semibold">
                                  Confirm New Password <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmNewPassword"
                                    className="form-control form-control-compact"
                                    value={confirmPassword}
                                    placeholder="Confirm new password"
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required={changePassword && !autoGeneratePassword}
                                    minLength={12}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    tabIndex={-1}
                                  >
                                    <i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="w-100 d-flex justify-content-end mt-4 pt-3 border-top">
                <Link to="/client-project-user">
                  <button type="button" className="btn btn-outline-secondary px-4">
                    Cancel
                  </button>
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary px-4 ms-2 d-flex align-items-center"
                  disabled={isLoadingSave}
                >
                  {isLoadingSave ? (
                    <>
                      <LoadingSpinner reverse={true} />
                      <span className="ms-2">Saving...</span>
                    </>
                  ) : (
                    <span>{isAddMode ? 'Send Invitation' : 'Save Changes'}</span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectUserForm;